import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';
import { useMyLeagueStore } from '../store/myLeagueStore';

/**
 * useTeam Hook
 * 
 * Provides team information fetching and updating capabilities.
 * 
 * Usage:
 * ```tsx
 * // For reading team data
 * const { team, isLoading, error, refetch } = useTeam(teamId);
 * 
 * // For updating team data
 * const { updateTeam, isUpdating, updateError } = useTeamUpdate(teamId);
 * 
 * // Example update
 * const handleUpdateTeam = async () => {
 *   try {
 *     await updateTeam({ name: 'New Team Name' });
 *   } catch (error) {
 *     console.error('Failed to update team:', error);
 *   }
 * };
 * ```
 */

export interface TeamInfo {
  id: number;
  division_id: number;
  parent_team_id?: number;
  name: string;
  rw_name?: string;
  abbreviation?: string;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
  stadium?: string;
  logo?: string;
  city?: string;
  state?: string;
  level?: string;
  won?: number;
  lost?: number;
  color_3?: string;
  salary_cap?: number;
  price: number;
  owned_by?: number;
  extra_info?: Record<string, unknown>;
  salaryCapDescription?: string | null;
  prefix?: string;
}

interface UseTeamReturn {
  team: TeamInfo | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

interface UseTeamUpdateReturn {
  updateTeam: (updateData: Partial<TeamInfo>) => Promise<TeamInfo>;
  isUpdating: boolean;
  updateError: Error | null;
}

export interface TeamMember {
  user_id: number;
  display_name: string;
  avatar?: string;
  roles: Array<{
    role_id: number;
    role_name: string;
  }>;
}

export interface CarriedSalary {
  id: number;
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1: string;
    position_2?: string;
    position_3?: string;
    mlb_team?: string;
    image?: string;
    bats?: string;
    throws?: string;
  };
  level: string;
  status: string;
  status_until: string | null;
  playerContract: {
    year_signed: number | null;
    current_salary: number;
    salaries: Array<{ year: number; salary: number }>;
    contract_types: Array<{ year: number; type: string }>;
    buyouts: Array<{ year: number; amount: number }>;
    total_value?: number;
  };
  is_40_man: boolean;
  year: number;
  trade_status?: string | null;
  trade_status_until?: string | null;
}

export interface Deal {
  expires: string | number | Date;
  viewed_at: string | null;
  id: number;
  league_id: number;
  created: string;
  updated: string;
  created_by?: number;
  type: 'trade' | 'claim_waiver' | 'free_agent_offer';
  status: 'proposed' | 'approved' | 'expired' | 'rejected' | 'under_review' | 'withdrawn' | 'declined' | 'accepted' | 'countered' | 'league_review' | 'has_salary_issue' | 'completed' | 'accepting_offers' | 'canceled' | 'settlement_trade';
  original?: number;
  parent?: number;
  child?: number;
  note?: string;
  team1?: number;
  players1?: number[];
  team1_accepted: boolean;
  team2?: number;
  players2?: number[];
  team2_accepted: boolean;
}

export interface Transaction {
  id: number;
  league_id?: number;
  team_id?: number;
  user_id?: number;
  player_id?: number;
  deal_id?: number;
  transaction_type_id: number;
  headline?: string;
  details?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface FrontOfficeData {
  management: TeamMember[];
  carriedSalaries: CarriedSalary[];
  pendingDeals: Deal[];
  recentTransactions: Transaction[];
}

interface UseFrontOfficeReturn {
  frontOffice: FrontOfficeData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

const fetchTeam = async (teamId: number): Promise<TeamInfo> => {
  const { data } = await apiClient.get(`/team/${teamId}`);
  return data;
};

const updateTeam = async ({ teamId, updateData }: { teamId: number; updateData: Partial<TeamInfo> }): Promise<TeamInfo> => {
  const { data } = await apiClient.put(`/team/${teamId}`, updateData);
  return data;
};

export const useTeam = (teamId: number | undefined): UseTeamReturn => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => fetchTeam(teamId!),
    enabled: !!teamId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
  });

  return {
    team: data ?? null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};

export const useTeamUpdate = (teamId: number | undefined): UseTeamUpdateReturn => {
  const queryClient = useQueryClient();
  
  const { mutateAsync: updateTeamMutation, isPending: isUpdating, error: updateError } = useMutation({
    mutationFn: (updateData: Partial<TeamInfo>) => updateTeam({ teamId: teamId!, updateData }),
    onSuccess: (updatedTeam) => {
      // Update the team data in the cache
      queryClient.setQueryData(['team', teamId], updatedTeam);
      // Invalidate related queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });

  return {
    updateTeam: updateTeamMutation,
    isUpdating,
    updateError: updateError as Error | null,
  };
};

const fetchFrontOffice = async (teamId: number, leagueId: number): Promise<FrontOfficeData> => {
  const { data } = await apiClient.get(`/team/front-office-info/${teamId}/${leagueId}`);
  return data;
};

export const useFrontOffice = (teamId: number | undefined): UseFrontOfficeReturn => {
  const myLeague = useMyLeagueStore((state) => state.myLeague);
  
  const leagueId = myLeague?.id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['frontOffice', teamId, leagueId],
    queryFn: () => fetchFrontOffice(teamId!, leagueId!),
    enabled: !!teamId && !!leagueId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
  });

  return {
    frontOffice: data ?? null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};

// Alias for backwards compatibility if needed
export const getFrontOffice = useFrontOffice; 